// Path grammar for the SPA. The SPA owns the root, so paths are
// workspace-first:
//
//   /                                → workspace=fleet (default), segments=[]
//   /fleet                           → workspace=fleet, segments=[]
//   /fleet/list                      → workspace=fleet, segments=['list']
//   /fleet/detail/rover-01           → workspace=fleet, segments=['detail','rover-01']
//   /fleet/teleop/rover-01           → workspace=fleet, segments=['teleop','rover-01']
//   /telemetry                       → workspace=telemetry
//   /commands                        → workspace=commands

export type Workspace = 'fleet' | 'telemetry' | 'commands' | 'catalog';

export const DEFAULT_WORKSPACE: Workspace = 'fleet';

export interface ParsedPath {
  workspace: Workspace;
  segments: string[];
}

const KNOWN_WORKSPACES: readonly Workspace[] = ['fleet', 'telemetry', 'commands', 'catalog'] as const;

function isWorkspace(value: string): value is Workspace {
  return (KNOWN_WORKSPACES as readonly string[]).includes(value);
}

function splitPath(pathname: string): string[] {
  if (pathname === '/' || pathname === '') return [];
  return pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/').filter((s) => s.length > 0);
}

export function parsePath(pathname: string): ParsedPath {
  const parts = splitPath(pathname);
  if (parts.length === 0) {
    return { workspace: DEFAULT_WORKSPACE, segments: [] };
  }
  const head = parts[0] ?? '';
  if (isWorkspace(head)) {
    return { workspace: head, segments: parts.slice(1) };
  }
  // Unknown leading segment — treat the whole path as segments under the
  // default workspace.
  return { workspace: DEFAULT_WORKSPACE, segments: parts };
}

export function buildPath(workspace: Workspace, ...segments: string[]): string {
  const tail = segments.filter((s) => s.length > 0).join('/');
  return tail ? `/${workspace}/${tail}` : `/${workspace}`;
}
